// Business Logic Layer - pure functions, no React. Mirrors
// _features/billingUnits/modals/BillingUnitFormModal/utils/
// billingUnitFormRules.js's shape. The databaseSelection dynamic-items
// injection stays inline in useUsageSourceFormSchema.js (mirroring where
// the old formSchemas file put it) rather than living here too.

// Unlike Market/Billing Unit (a single, purely decorative "<Entity>
// details" heading, stripped outright), this schema's per-origin sections
// ("MongoDB URL", "MySQL Details", "Google Sheet URL") are load-bearing -
// they're the only thing telling the user which connector the fields below
// belong to once they've picked an origin source. Only the top, generic
// "Basic details" heading (an org-customization holdover with no
// equivalent in the Companies Workspace visual language) gets blanked;
// every other section heading is left alone.
function stripGenericSectionHeading(sections) {
  return sections.map((section) =>
    section.section === "Basic details" ? { ...section, section: "" } : section
  );
}

// Status carries the same explicit `placeholder: ""` override bug fixed
// for Markets'/Billing Units' own Status field - see their
// applyFieldPlaceholders comments. Every other field here (title,
// description, originSource, and every per-connector field: host,
// username, password, database, databaseType, mongoDbUrl,
// googleSheetUrl...) already has a real placeholder from
// object-customization data, so this map only needs the one entry.
const FIELD_PLACEHOLDERS = {
  status: "Select",
};
function applyFieldPlaceholders(sections) {
  return sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) =>
      FIELD_PLACEHOLDERS[field.name] !== undefined
        ? { ...field, placeholder: FIELD_PLACEHOLDERS[field.name] }
        : field
    ),
  }));
}

// Same "pack everything but a full-width field two-per-row" rule Markets'/
// Billing Units' own packFieldsForTwoColumnGrid follows, applied
// independently within every section (not just "Basic details") - MySQL
// Details' 5 short fields (host/username/password/database/databaseType)
// benefit from the same packing "Basic details" gets. `originSource` is
// treated as full-width alongside textArea fields (not packed next to
// Status) since it's the switch that determines which section renders
// below it - visually anchoring it as its own row reads better than
// leaving it to dangle alone in a half-empty row. Its type (a plain
// select) isn't in DynamicForm's own FULL_WIDTH_FIELD_TYPES, so it's
// stamped with the `fullWidth: true` opt-in override that component reads
// alongside that type list (see its own comment).
//
// A section with exactly one field (Google Sheet URL's googleSheetUrl) is
// also forced full-width regardless of type/name - a lone connection-string
// input squeezed into half the grid with an empty gap beside it read as
// broken chrome, not a deliberate half-width field. MongoDB URL's
// mongoDbUrl needs the same treatment but can't rely on that rule alone:
// its section also carries databaseSelection (only shown post-validation,
// via showCondition - see useUsageSourceFormSchema.js), so the section's
// raw field count is 2 even while mongoDbUrl renders alone. Caught here by
// type instead ("url" fields are always connection strings, never a short
// value worth half a row).
function packFieldsForTwoColumnGrid(sections) {
  const isFullWidth = (field) => field.type === "textArea" || field.type === "url" || field.name === "originSource";
  return sections.map((section) => {
    const fields = section.fields || [];
    if (fields.length === 1) {
      return { ...section, fields: [{ ...fields[0], fullWidth: true }] };
    }
    const fullWidthFields = fields.filter(isFullWidth).map((f) => ({ ...f, fullWidth: true }));
    const packedFields = fields.filter((f) => !isFullWidth(f));
    return { ...section, fields: [...packedFields, ...fullWidthFields] };
  });
}

/**
 * Applies every UsageSourceFormModal-specific schema post-processing rule
 * to the raw org-customization schema.
 */
export function processUsageSourceFormSchema(schema) {
  return stripGenericSectionHeading(packFieldsForTwoColumnGrid(applyFieldPlaceholders(schema)));
}

/**
 * Derives the create/update success-toast message from the submitted
 * form data - same "{title} - Usage source successfully
 * {created/updated}" rule the original inline handlers duplicated once
 * for create and once for edit. Uses the submitted title (not the
 * response) since the old code did too - the create response doesn't
 * reliably echo back `title` in every origin-type branch.
 */
export function formatUsageSourceSuccessMessage(validData, mode) {
  return `${validData?.title} - Usage source successfully ${mode === "create" ? "created" : "updated"}`;
}
